import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Upload, Tabs, Radio } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from '../../common/Popover';
import { uploadMediaWithMeta } from '../../utils/campaignUtils';
import { useCampaign } from '../../context/CampaignContext';

const { TextArea } = Input;

export const AdCreativePanel: React.FC = () => {
    const { state, dispatch } = useCampaign();
    const [uploadLoading, setUploadLoading] = useState(false);


    const handleUpload = async (file: File) => {
        setUploadLoading(true)
        const currentMediaType = state.ads[state.currentAdIndex].mediaType;

        // Validate file type based on media type
        if (currentMediaType === 'image' && !file.type.startsWith('image/')) {
            toast('error', 'Please upload an image file');
            return Upload.LIST_IGNORE;
        }

        if (currentMediaType === 'video' && !file.type.startsWith('video/')) {
            toast('error', 'Please upload a video file');
            return Upload.LIST_IGNORE;
        }

        // Validate aspect ratio (1:1) for images
        if (file.type.startsWith('image/')) {
            const isSquare = await new Promise<boolean>((resolve) => {
                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                    const width = img.width;
                    const height = img.height;
                    URL.revokeObjectURL(img.src);
                    // Allow small tolerance for floating point issues
                    // const ratio = width / height;
                    // if (Math.abs(ratio - 1) < 0.1) {
                    //     resolve(true);
                    // } else {
                    //     resolve(false);
                    // }
                    resolve(true);
                };
                img.onerror = () => resolve(false);
            });

            if (!isSquare) {
                toast('error', 'Image must have a 1:1 aspect ratio (square).');
                return Upload.LIST_IGNORE;
            }
        }

        try {
            const uploadedMedia = await uploadMediaWithMeta(file, state.orgId, state.selectedAdAccountId);
            if (!uploadedMedia.url) throw new Error('Upload failed');

            if (currentMediaType === 'image') {
                dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaUrl', value: uploadedMedia.url });
                dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: uploadedMedia.imageSource || 's3' });
                dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaImageHash', value: uploadedMedia.hash || '' });
            } else {
                dispatch({ type: 'UPDATE_AD_FIELD', field: 'videoId', value: uploadedMedia.url });
            }
            return false;
        } catch (error) {
            toast('error', 'Failed to upload media');
            return false;
        } finally {
            setUploadLoading(false)
        }
    };

    const handleThumbnailUpload = async (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast('error', 'Thumbnail must be an image file');
            return Upload.LIST_IGNORE;
        }

        // Validate aspect ratio (1:1)
        const isSquare = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                URL.revokeObjectURL(img.src);
                const ratio = width / height;
                if (Math.abs(ratio - 1) < 0.01) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            };
            img.onerror = () => resolve(false);
        });

        if (!isSquare) {
            toast('error', 'Thumbnail must have a 1:1 aspect ratio (square).');
            return Upload.LIST_IGNORE;
        }

        try {
            const uploadedMedia = await uploadMediaWithMeta(file, state.orgId, state.selectedAdAccountId);
            if (!uploadedMedia.url) throw new Error('Upload failed');
            dispatch({ type: 'UPDATE_AD_FIELD', field: 'thumbnailUrl', value: uploadedMedia.url });
            dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaUrl', value: uploadedMedia.url });
            dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: uploadedMedia.imageSource || 's3' });
            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: uploadedMedia.hash || '' });
            return false;
        } catch (error) {
            toast('error', 'Failed to upload thumbnail');
            return false;
        }
    };

    const currentAd = state.ads[state.currentAdIndex];

    const items = state.ads.map((ad, index) => ({
        key: index.toString(),
        label: ad.adName || `Ad ${index + 1}`,
        closable: state.ads.length > 1,
    }));

    const onEdit = (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: 'add' | 'remove') => {
        if (action === 'add') {
            dispatch({ type: 'ADD_AD' });
        } else {
            dispatch({ type: 'REMOVE_AD', index: parseInt(targetKey as string) });
        }
    };

    return (
        <div className="mt-4">
            <Tabs
                type="editable-card"
                onChange={(key) => dispatch({ type: 'SET_CURRENT_AD_INDEX', index: parseInt(key) })}
                activeKey={state.currentAdIndex.toString()}
                onEdit={onEdit}
                items={items}
                className="mb-4"
                addIcon={<div className="flex items-center gap-1"><PlusOutlined /> Add Ad</div>}
                hideAdd={state.isEditing || state.ads.length == 3}

            />

            <Form layout="vertical" className="space-y-4" disabled={state.isEditing}>
                <Form.Item label="Ad Name" required>
                    <Input
                        value={currentAd.adName}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'adName', value: e.target.value })}
                        placeholder="Enter ad name"
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item label="Primary Text" required>
                    <TextArea
                        value={currentAd.primaryText}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'primaryText', value: e.target.value })}
                        placeholder="Enter primary text"
                        rows={4}
                        className="rounded-md"
                        disabled={state.isEditing}
                    />
                </Form.Item>

                <Form.Item label="Headline" required>
                    <Input
                        value={currentAd.headline}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'headline', value: e.target.value })}
                        placeholder="Enter headline"
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item label="Description">
                    <TextArea
                        value={currentAd.descriptionText}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'descriptionText', value: e.target.value })}
                        placeholder="Enter description"
                        rows={2}
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item label="Prefilled Message">
                    <TextArea
                        value={currentAd.prefilledMessage}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'prefilledMessage', value: e.target.value })}
                        placeholder="Enter prefilled message"
                        rows={2}
                        className="rounded-md"
                        required={false}
                    />
                </Form.Item>

                <Form.Item label="Welcome Message">
                    <TextArea
                        value={currentAd.welcomeMessage}
                        onChange={(e) => dispatch({ type: 'UPDATE_AD_FIELD', field: 'welcomeMessage', value: e.target.value })}
                        placeholder="Enter welcome message"
                        rows={2}
                        className="rounded-md"
                        required={false}
                    />
                </Form.Item>

                <Form.Item label="Media Type" required>
                    <Radio.Group
                        value={currentAd.mediaType}
                        onChange={(e) => {
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaType', value: e.target.value });
                            // Clear media fields when switching type
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaUrl', value: '' });
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'videoId', value: '' });
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'thumbnailUrl', value: '' });
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: 's3' });
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaImageHash', value: '' });
                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: '' });
                        }}
                    >
                        <Radio value="image">Image</Radio>
                        <Radio value="video">Video</Radio>
                    </Radio.Group>
                </Form.Item>

                {currentAd.mediaType === 'image' ? (
                    <Form.Item label="Image" required>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <Row gutter={16} align="middle">
                                <Col span={12} className="flex items-center gap-4">
                                    <Upload
                                        maxCount={1}
                                        accept="image/jpg,image/jpeg,image/png"
                                        beforeUpload={handleUpload}
                                        onRemove={() => {
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaUrl', value: '' });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: 's3' });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaImageHash', value: '' });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: '' });
                                        }}
                                        showUploadList={false}
                                    >
                                        <Button>
                                            Upload Image
                                        </Button>
                                    </Upload>
                                    <span className="text-gray-400">or</span>
                                </Col>
                                <Col span={12}>
                                    <Input
                                        placeholder="Enter image URL"
                                        value={currentAd.mediaUrl}
                                        onChange={(e) => {
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'mediaUrl', value: e.target.value });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: 's3' });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaImageHash', value: '' });
                                            dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: '' });
                                        }}
                                        className="rounded-md"
                                    />
                                </Col>
                            </Row>
                            {currentAd.mediaUrl && (
                                <div className="mt-4">
                                    <img
                                        src={currentAd.mediaUrl}
                                        alt="Ad Media"
                                        className="w-32 h-32 object-cover rounded-md border border-gray-300"
                                    />
                                </div>
                            )}
                        </div>
                    </Form.Item>
                ) : (
                    <>
                        <Form.Item label="Video" required>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <Upload
                                    maxCount={1}
                                    accept="video/mp4,video/quicktime"
                                    beforeUpload={handleUpload}
                                    onRemove={() => {
                                        dispatch({ type: 'UPDATE_AD_FIELD', field: 'videoId', value: '' });
                                    }}
                                    showUploadList={false}
                                >
                                    <Button loading={uploadLoading}>
                                        Upload Video
                                    </Button>
                                </Upload>
                                {currentAd.videoId && (
                                    <div className="mt-2 text-sm text-green-600">
                                        ✓ Video uploaded successfully
                                    </div>
                                )}
                            </div>
                        </Form.Item>

                        <Form.Item label="Thumbnail" required>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <Row gutter={16} align="middle">
                                    <Col span={12} className="flex items-center gap-4">
                                        <Upload
                                            maxCount={1}
                                            accept="image/jpg,image/jpeg,image/png"
                                            beforeUpload={handleThumbnailUpload}
                                            onRemove={() => {
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'thumbnailUrl', value: '' });
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: 's3' });
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: '' });
                                            }}
                                            showUploadList={false}
                                        >
                                            <Button>
                                                Upload Thumbnail
                                            </Button>
                                        </Upload>
                                        <span className="text-gray-400">or</span>
                                    </Col>
                                    <Col span={12}>
                                        <Input
                                            placeholder="Enter thumbnail URL"
                                            value={currentAd.thumbnailUrl}
                                            onChange={(e) => {
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'thumbnailUrl', value: e.target.value });
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'imageSource', value: 's3' });
                                                dispatch({ type: 'UPDATE_AD_FIELD', field: 'metaThumbnailImageHash', value: '' });
                                            }}
                                            className="rounded-md"
                                        />
                                    </Col>
                                </Row>
                                {currentAd.thumbnailUrl && (
                                    <div className="mt-4">
                                        <img
                                            src={currentAd.thumbnailUrl}
                                            alt="Video Thumbnail"
                                            className="w-32 h-32 object-cover rounded-md border border-gray-300"
                                        />
                                    </div>
                                )}
                            </div>
                        </Form.Item>
                    </>
                )}
            </Form>
        </div>
    );
};
